<template>
  <div v-if="kpiData.type === 'knob'">
    <div class="kpi-header">
      {{ kpiName }}
    </div>
    <div class="kpi-knob-container">
      <Knob
        v-if="hasData"
        v-model="dataValue"
        readonly="true"
        size="350"
        stroke-width="10"
      />
      <StatsEmptyState v-else />
    </div>
  </div>
  <div
    v-else
    class="kpi-widget"
  >
    <div class="kpi-header">
      {{ kpiName }}
    </div>
    <div class="kpi-content">
      <div
        v-if="hasData"
        ref="kpiGridRef"
        class="kpi-grid"
        :class="[getGridClass, { 'kpi-grid--scrolled-bottom': isScrolledToBottom, 'kpi-grid--no-scroll': !hasScroll }]"
      >
        <div
          v-for="(kpi, index) in data"
          :key="index"
          v-tooltip.bottom="kpi.legend"
          class="kpi-card"
          :class="{ 'kpi-card--total': kpi.isTotal, 'kpi-card--has-icon': kpi.icon || kpi.isTotal }"
        >
          <div class="kpi-card-inner">
            <div
              v-if="kpi.icon || kpi.isTotal"
              class="kpi-icon-badge"
              :style="getIconBadgeStyle(kpi, index)"
            >
              <i
                :class="kpi.icon || 'fa-solid fa-calculator'"
              />
            </div>
            <div class="kpi-details">
              <div
                v-if="kpi.label"
                class="kpi-label"
              >
                {{ kpi.label }}
              </div>
              <div
                class="kpi-value"
                :style="kpi.color ? `color: ${kpi.color};` : ''"
                v-html="kpi.value"
              />
            </div>
          </div>
        </div>
      </div>
      <StatsEmptyState v-else />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  Ref,
  ref,
} from 'vue';
import { UserState } from '@/composables/User';
import { DEFAULT_CURRENCY } from '@/components/template-builder/utils/constants';
import { formatNumbersInString, formatNumberToCurrency } from '@/helpers/Number';
import Knob from 'primevue/knob';
import type { SpmKpiDataPoint } from '@/types/stats-types';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import {
  getStatsLabelKeyFromSlug,
  normalizeStatsLabelSlug,
} from '@/helpers/statsLabels';
import type { StatsLabelContext } from '@/helpers/statsLabels';
import StatsEmptyState from '@/components/stats/StatsEmptyState.vue';

export default defineComponent({
  name: 'SpmKpi',

  components: {
    Knob,
    StatsEmptyState,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    kpiName: {
      type: String,
      required: true,
    },

    kpiData: {
      type: Array as PropType<SpmKpiDataPoint[]>,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const data: Ref<SpmKpiDataPoint[]> = ref(props.kpiData);
    const kpiGridRef = ref<HTMLElement | null>(null);
    const isScrolledToBottom = ref(false);
    const hasScroll = ref(false);

    const statsIcons: Record<StatsLabelContext | string, Record<string, string>> = {
      channels: {
        email: 'fa-regular fa-envelope',
        display: 'fa-regular fa-desktop',
        popup: 'fa-regular fa-share',
        embed: 'fa-regular fa-list-alt',
        pushnotifications: 'fa-regular fa-bell',
        sms: 'fa-regular fa-sms',
        whatsapp: 'fa-brands fa-whatsapp',
        richtexting: 'fa-regular fa-message-smile',
      },

      boxes: {
        email: 'fa-regular fa-envelope',
        embed: 'fa-regular fa-list-alt',
        popup: 'fa-regular fa-share',
        pushnotifications: 'fa-regular fa-bell',
        sms: 'fa-regular fa-sms',
        rcs: 'fa-regular fa-message-smile',
        whatsapp: 'fa-brands fa-whatsapp',
      },

      registrations: {
        registration: 'fa-regular fa-store',
        newsletter: 'fa-regular fa-envelope',
      },

      bouncesTypesIntegers: {
        'hard-bounce': 'fa-regular fa-ban',
        'soft-bounce': 'fa-regular fa-triangle-exclamation',
        spam: 'fa-regular fa-shield-exclamation',
        unsubscription: 'fa-regular fa-user-slash',
      },
    };

    const hasData = data.value.length > 0;

    const translateStatLabel = (
      value: string | null | undefined,
      context?: StatsLabelContext,
    ): string => {
      if (!value) {
        return '';
      }

      const translationKey = getStatsLabelKeyFromSlug(value, context);
      return translationKey.includes('.') ? t(translationKey) : translationKey;
    };

    data.value.forEach((kpi) => {
      const current = kpi;

      const context = (typeof current.icon === 'string'
        ? current.icon
        : undefined) as StatsLabelContext | undefined;

      const slug = typeof current.label === 'string'
        ? normalizeStatsLabelSlug(current.label)
        : '';

      const iconGroup = context ? statsIcons[context] : undefined;
      const icon = iconGroup ? iconGroup[slug] ?? null : null;

      if (current.label && !current.skipLabelTranslation) {
        current.label = translateStatLabel(current.label, context);
      }

      if (current.legend && !current.skipLabelTranslation) {
        current.legend = translateStatLabel(current.legend, context);
      }

      if (icon) {
        current.icon = icon;
      }

      // Les valeurs sont déjà formatées par l'API, pas besoin de les reformater
      // Le formatage était : formatNumberToCurrency() pour type 'price' et formatNumbersInString() pour type 'number'
      // Maintenant on garde la valeur telle quelle
    });

    const iconColors = [
      { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }, // Blue
      { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }, // Green
      { bg: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }, // Orange
      { bg: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }, // Cyan
      { bg: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }, // Pink
      { bg: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }, // Purple
      { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }, // Amber
      { bg: 'rgba(20, 184, 166, 0.1)', color: '#14b8a6' }, // Teal
    ];

    const getIconBadgeStyle = (kpi: SpmKpiDataPoint, index: number) => {
      if (kpi.isTotal) {
        // Pas de style inline pour les cards Total, le CSS s'en occupe
        return {};
      }

      const colorSet = iconColors[index % iconColors.length];
      return {
        backgroundColor: colorSet.bg,
        color: colorSet.color,
      };
    };

    const getGridClass = computed(() => {
      const kpiCount = data.value.length;

      if (kpiCount === 1) {
        return 'kpi-grid--single';
      }
      if (kpiCount === 2) {
        return 'kpi-grid--double';
      }
      if (kpiCount === 3) {
        return 'kpi-grid--triple';
      }
      if (kpiCount === 4) {
        return 'kpi-grid--quad';
      }

      return 'kpi-grid--list';
    });

    const handleScroll = () => {
      if (!kpiGridRef.value) return;

      const { scrollTop, scrollHeight, clientHeight } = kpiGridRef.value;

      // Vérifier si le scroll est nécessaire (contenu dépasse la hauteur visible)
      hasScroll.value = scrollHeight > clientHeight;

      // Considère qu'on est en bas si on est à moins de 10px du bas
      isScrolledToBottom.value = scrollTop + clientHeight >= scrollHeight - 10;
    };

    onMounted(() => {
      if (kpiGridRef.value) {
        kpiGridRef.value.addEventListener('scroll', handleScroll);
        // Vérifier l'état initial après un court délai pour que le rendu soit complet
        setTimeout(handleScroll, 100);
      }

      // Recalculer si le scroll est nécessaire quand la fenêtre change de taille
      window.addEventListener('resize', handleScroll);
    });

    onUnmounted(() => {
      if (kpiGridRef.value) {
        kpiGridRef.value.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', handleScroll);
    });

    return {
      t,
      hasData,
      data,
      getIconBadgeStyle,
      getGridClass,
      kpiGridRef,
      isScrolledToBottom,
      hasScroll,
    };
  },
});
</script>

<style scoped lang="scss">
// Header
.kpi-header {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color, #374151);
  margin-bottom: 1.25rem;
  padding: 0 0.5rem;

  @media (max-width: 1366px) {
    font-size: 0.9375rem;
    margin-bottom: 0.75rem;
    padding: 0 0.375rem;
  }
}

// Knob container (type='knob')
.kpi-knob-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0;

  .p-knob:deep() {
    .p-knob-value {
      stroke: $knobValueStroke !important;
    }

    .p-knob-text {
      opacity: 0.6 !important;
    }
  }
}

// Widget container
.kpi-widget {
  height: 100%;
  display: flex;
  flex-direction: column;
}

// Content container - Prend toute la hauteur disponible
.kpi-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden; // Par défaut, pas de scroll
  min-height: 0; // Important pour le flex
  max-height: 100%; // Limite la hauteur pour permettre le scroll enfant
  position: relative; // Pour le positionnement absolu du dégradé
}

// Grid layout - Adaptatif selon le nombre de KPIs
.kpi-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem; // Gap optimisé pour meilleure utilisation de l'espace
  padding: 0.25rem;
  height: 100%; // Force la hauteur totale

  @media (max-width: 1366px) {
    gap: 0.375rem; // Gap réduit pour 720p
    padding: 0.125rem;
  }

  // 1 KPI : Prend tout l'espace
  &--single {
    .kpi-card {
      .kpi-card-inner {
        padding: 2rem;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 1.5rem;
      }

      .kpi-icon-badge {
        width: 52px;
        height: 52px;
        font-size: 1.625rem;
      }

      .kpi-label {
        font-size: 0.875rem;
      }

      .kpi-value {
        font-size: 1.5rem;
      }
    }
  }

  // 2 KPIs : 50/50
  &--double {
    flex-direction: row;

    .kpi-card {
      flex: 1;
      justify-content: center;

      .kpi-card-inner {
        padding: 1rem;
        height: auto;
        gap: 0.5rem;
      }

      .kpi-icon-badge {
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
      }

      .kpi-label {
        font-size: 0.8125rem;
      }

      .kpi-value {
        font-size: 1.25rem;
      }
    }
  }

  // 3 KPIs : 2 en haut (50/50), 1 en bas (100%)
  &--triple {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;

    .kpi-card {
      justify-content: center;

      .kpi-card-inner {
        padding: 0.875rem;
        height: auto;
        gap: 0.5rem;
      }

      .kpi-icon-badge {
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
      }

      .kpi-label {
        font-size: 0.8125rem;
      }

      .kpi-value {
        font-size: 1.25rem;
      }

      // La 3ème card prend toute la largeur
      &:nth-child(3) {
        grid-column: 1 / -1;

        .kpi-card-inner {
          flex-direction: row;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.5rem;
        }

        .kpi-icon-badge {
          width: 40px;
          height: 40px;
          font-size: 1.25rem;
        }

        .kpi-details {
          gap: 0.25rem;
        }

        .kpi-label {
          font-size: 0.8125rem;
        }

        .kpi-value {
          font-size: 1.25rem;
        }
      }
    }
  }

  // 4 KPIs : 2x2 grille flexible
  &--quad {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);

    .kpi-card {
      justify-content: center;

      .kpi-card-inner {
        padding: 0.875rem;
        height: auto;
        gap: 0.5rem;
      }

      .kpi-icon-badge {
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
      }

      .kpi-label {
        font-size: 0.8125rem;
      }

      .kpi-value {
        font-size: 1.25rem;
      }
    }
  }

  // 5+ KPIs : Liste verticale avec scroll
  &--list {
    flex-direction: column;
    gap: 0.375rem; // Gap réduit pour optimiser l'espace
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 0.5rem;
    height: auto; // Override du height: 100% du parent pour permettre le scroll
    max-height: 400px; // Hauteur maximale fixe pour forcer le scroll au-delà de ~5-6 KPIs
    padding-bottom: 0.5rem; // Petit espace pour que le dernier élément ne soit pas collé au bord

    // Dégradé blanc en bas pour indiquer qu'il y a plus de contenu
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 60px;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.95) 70%, rgba(255, 255, 255, 1) 100%);
      pointer-events: none; // Ne bloque pas les interactions
      z-index: 10;
      opacity: 1;
      transition: opacity 0.2s ease;
    }

    // Masquer le dégradé quand on est en bas du scroll
    &.kpi-grid--scrolled-bottom::before {
      opacity: 0;
    }

    // Masquer le dégradé quand il n'y a pas de scroll (tout le contenu est visible)
    &.kpi-grid--no-scroll::before {
      opacity: 0;
    }

    @media (max-width: 1366px) {
      gap: 0.125rem; // Gap ultra compact en 720p
      padding-right: 0.375rem;
      max-height: 280px; // Hauteur réduite pour 720p
      padding-bottom: 0.375rem;

      &::before {
        height: 50px; // Dégradé légèrement plus petit en 720p
      }
    }

    // Scroll bar personnalisée
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: var(--surface-ground, #f9fafb);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--surface-border, #d1d5db);
      border-radius: 3px;

      &:hover {
        background: var(--text-color-secondary, #9ca3af);
      }
    }

    .kpi-card {
      flex: 0 0 auto; // Hauteur automatique basée sur le contenu
      min-height: 60px; // Hauteur minimale pour chaque carte
      padding: 0.5rem 0.75rem;

      .kpi-card-inner {
        flex-direction: row;
        align-items: center;
        gap: 0.75rem;
        padding: 0.25rem; // Padding réduit
      }

      .kpi-icon-badge {
        width: 36px;
        height: 36px;
        font-size: 1.125rem;
        flex-shrink: 0;
      }

      .kpi-details {
        flex-direction: row;
        align-items: center;
        gap: 0.75rem;
        flex: 1;
      }

      .kpi-label {
        font-size: 0.8125rem;
        font-weight: 500;
        flex: 0 0 auto;
        max-width: 40%;
        -webkit-line-clamp: 1;
      }

      .kpi-value {
        font-size: 1.25rem;
        flex: 1;
        text-align: right;
      }

      &--total {
        grid-column: 1;

        .kpi-value {
          font-size: 1.5rem;
        }
      }
    }
  }

  // Responsive pour écrans moyens (720p - 900p)
  @media (max-width: 1366px) {
    &--single {
      .kpi-card {
        .kpi-card-inner {
          padding: 0.875rem;
          gap: 0.875rem;
        }

        .kpi-icon-badge {
          width: 42px;
          height: 42px;
          font-size: 1.3125rem;
        }

        .kpi-details {
          gap: 0.25rem;
        }

        .kpi-label {
          font-size: 0.75rem;
        }

        .kpi-value {
          font-size: 1.125rem;
        }
      }
    }

    &--double {
      .kpi-card {
        .kpi-card-inner {
          padding: 0.5rem;
          gap: 0.3125rem;
        }

        .kpi-icon-badge {
          width: 34px;
          height: 34px;
          font-size: 1.0625rem;
        }

        .kpi-details {
          gap: 0.1875rem;
        }

        .kpi-label {
          font-size: 0.6875rem;
        }

        .kpi-value {
          font-size: 1rem;
        }
      }
    }

    &--triple {
      .kpi-card {
        .kpi-card-inner {
          padding: 0.4375rem;
          gap: 0.3125rem;
        }

        .kpi-icon-badge {
          width: 34px;
          height: 34px;
          font-size: 1.0625rem;
        }

        .kpi-details {
          gap: 0.1875rem;
        }

        .kpi-label {
          font-size: 0.6875rem;
        }

        .kpi-value {
          font-size: 1rem;
        }

        // La 3ème card prend toute la largeur (conservé en 720p)
        &:nth-child(3) {
          grid-column: 1 / -1;

          .kpi-card-inner {
            flex-direction: row; // Mode horizontal en 720p
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem 1rem;
          }

          .kpi-icon-badge {
            width: 34px;
            height: 34px;
            font-size: 1.0625rem;
          }

          .kpi-details {
            gap: 0.1875rem;
          }

          .kpi-label {
            font-size: 0.6875rem;
          }

          .kpi-value {
            font-size: 1rem;
          }
        }
      }
    }

    &--quad {
      .kpi-card {
        .kpi-card-inner {
          padding: 0.4375rem;
          gap: 0.3125rem;
        }

        .kpi-icon-badge {
          width: 34px;
          height: 34px;
          font-size: 1.0625rem;
        }

        .kpi-details {
          gap: 0.1875rem;
        }

        .kpi-label {
          font-size: 0.6875rem;
        }

        .kpi-value {
          font-size: 1rem;
        }
      }
    }

    &--list {
      .kpi-card {
        padding: 0.25rem 0.5rem;

        .kpi-card-inner {
          padding: 0.125rem;
          gap: 0.5rem;
        }

        .kpi-icon-badge {
          width: 28px;
          height: 28px;
          font-size: 0.875rem;
        }

        .kpi-details {
          gap: 0.5rem;
        }

        .kpi-label {
          font-size: 0.6875rem;
        }

        .kpi-value {
          font-size: 1rem;
        }
      }
    }
  }

  // Responsive pour petits écrans
  @media (max-width: 1024px) {
    &--quad {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    &--quad {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }

    &--double {
      flex-direction: column;
    }
  }

  @media (max-width: 640px) {
    &--double,
    &--triple,
    &--quad {
      display: flex;
      flex-direction: column;
    }

    // Réinitialiser le grid-column pour le mode colonne
    &--triple .kpi-card:nth-child(3) {
      grid-column: auto;
    }
  }
}

// Card KPI - Styles de base
.kpi-card {
  background: var(--surface-card, #ffffff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 0.625rem;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  flex: 1; // Prend une portion égale de l'espace disponible
  min-height: 0; // Important pour le flex

  @media (max-width: 1366px) {
    border-radius: 0.5rem; // Border-radius réduit en 720p
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
    border-color: rgba(59, 130, 246, 0.3);
  }

  // Style spécifique pour la card Total
  &--total {
    border: 1.5px solid rgba(59, 130, 246, 0.25);
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.04) 0%, rgba(147, 51, 234, 0.04) 100%);

    .kpi-value {
      font-weight: 700;
    }

    .kpi-icon-badge {
      background: rgba(59, 130, 246, 0.1);
      color: rgba(59, 130, 246, 1);
    }
  }
}

// Card inner layout
.kpi-card-inner {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  padding: 0.625rem; // Padding par défaut optimisé
  justify-content: center; // Centre le contenu verticalement

  @media (max-width: 1366px) {
    padding: 0.5rem; // Padding réduit en 720p
    gap: 0.375rem;
  }
}

// Icon badge
.kpi-icon-badge {
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s ease;

  @media (max-width: 1366px) {
    border-radius: 0.375rem; // Border-radius réduit en 720p
  }

  .kpi-card:hover & {
    transform: scale(1.08);
  }
}

// Details container
.kpi-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
  justify-content: center; // Centre verticalement le contenu

  @media (max-width: 1366px) {
    gap: 0.25rem; // Gap réduit en 720p
  }
}

// Label
.kpi-label {
  font-weight: 500;
  color: var(--text-color-secondary, #6b7280);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1; // Force sur une seule ligne
  -webkit-box-orient: vertical;

  @media (max-width: 1366px) {
    line-height: 1.15; // Line-height réduit en 720p
  }
}

// Value
.kpi-value {
  font-weight: 700;
  color: var(--text-color, #1f2937);
  line-height: 1.1;
  word-break: break-word;

  @media (max-width: 1366px) {
    line-height: 1.05; // Line-height très compact en 720p
  }

  // Valeur secondaire (entre parenthèses)
  &:deep() div {
    display: block;
    font-size: 0.8875rem;
    font-weight: 500;
    color: #9ca3af;
    margin-top: 0.125rem;
    margin-left: 0;

    @media (max-width: 1366px) {
      font-size: 0.625rem; // Taille réduite en 720p
      margin-top: 0.0625rem; // Marge réduite en 720p
      line-height: 1;
    }
  }
}
</style>
